import { CrownIcon, ShieldCheckIcon } from 'lucide-react';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { Avatar, Badge } from '@splitfiesta/ui';

import { PlaceholderBadge } from '@/components/members/PlaceholderBadge';
import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';

import { resolveUserName } from '@/lib/displayName';

interface MemberBalance {
  id: string;
  name: string;
  profileImage?: string;
  joinedAt?: string;
  role?: string;
  isPlaceholder?: boolean;
  /** Already gated by the `pro` flag in the container. */
  isSupporter?: boolean;
  balance: {
    amount: number;
    currency: string;
    isPositive: boolean;
  };
}

interface MemberBalanceListProps {
  members: MemberBalance[];
  currentUserId?: string;
  groupId: string;
  onMemberClick?: (memberId: string) => void;
}

export function MemberBalanceList({
  members,
  currentUserId,
  groupId: _groupId,
  onMemberClick,
}: MemberBalanceListProps) {
  const { t } = useTranslation();

  return (
    <div className="-mx-6 sm:-mx-6" data-testid="member-balance-list">
      {members.map((member, index) => {
        const isCurrentUser = member.id === currentUserId;
        const isOwner = member.role === 'owner';
        const isAdmin = member.role === 'admin';
        const hasBalance = member.balance.amount !== 0;

        return (
          <div
            key={member.id}
            className={`py-3 sm:py-3 flex items-center justify-between gap-3 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors px-6 relative ${
              index !== members.length - 1
                ? 'border-b border-gray-100 dark:border-gray-700'
                : ''
            }`}
            onClick={() => onMemberClick?.(member.id)}
            data-testid={`member-balance-item-${member.id}`}
          >
            {/* Left: Member Info */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Avatar
                name={member.name}
                src={member.profileImage}
                size="md"
                className="shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <h4 className="flex min-w-0 items-center text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                    <span className="min-w-0 truncate">
                      {resolveUserName(member.name, t)}
                      {isCurrentUser && (
                        <span className="text-gray-500 dark:text-gray-400 font-normal ml-1">
                          ({t('common:labels.you')})
                        </span>
                      )}
                    </span>
                    {member.isSupporter && (
                      <SupporterBadge muted className="ml-1 shrink-0" />
                    )}
                  </h4>
                  {member.isPlaceholder && <PlaceholderBadge />}
                  {isOwner && (
                    <Badge
                      variant="warning"
                      size="sm"
                      className="shrink-0 w-fit"
                    >
                      <CrownIcon className="w-3 h-3 mr-1" />
                      {t('members:roles.owner')}
                    </Badge>
                  )}
                  {isAdmin && !isOwner && (
                    <Badge variant="info" size="sm" className="shrink-0 w-fit">
                      <ShieldCheckIcon className="w-3 h-3 mr-1" />
                      {t('members:roles.admin')}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Balance */}
            <div
              className="shrink-0 text-right"
              data-testid={`balance-amount-${member.id}`}
            >
              {!hasBalance ? (
                <div data-testid={`balance-settled-${member.id}`}>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('common:labels.settled')}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {member.balance.currency}
                  </p>
                </div>
              ) : (
                <div data-testid={`balance-details-${member.id}`}>
                  <p
                    className={`text-base sm:text-lg font-bold ${
                      member.balance.isPositive
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                    data-testid={`balance-value-${member.id}`}
                  >
                    {member.balance.isPositive ? '+' : '-'}
                    <CurrencyDisplay
                      amount={member.balance.amount}
                      currency={member.balance.currency}
                    />
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {member.balance.isPositive
                      ? t('common:labels.getsBack')
                      : t('common:labels.owes')}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
